import React, {useEffect, useReducer, useState} from 'react';

import {AdapterField, ConnectionFieldType} from 'instruments/connections/ConnectionConstants';
import {getAdapter} from 'instruments/connections/ConnectionDAO';
import TextField from 'ui-library/inputs/TextField';

import {GATEWAY_TYPE_TO_INSTRUMENT_TYPE, GatewayType, S3_FIELD_IDS} from './InstrumentGatewayConstants';
import {BUCKET_ERROR, isValidBucket, isValidRoleArn, ROLE_ARN_ERROR} from './S3GatewayValidators';

// Loads the gateway-scoped adapter fields for a given gateway type. Returns
// `fields: null` while loading, and `fields: []` if the gateway type has no
// adapter-driven fields.
export function useGatewayFields(gatewayType: GatewayType): {fields: Array<AdapterField> | null} {
  const [fields, setFields] = useState<Array<AdapterField> | null>(null);

  useEffect(() => {
    const instrumentType = GATEWAY_TYPE_TO_INSTRUMENT_TYPE[gatewayType];
    if (!instrumentType) {
      setFields([]);
      return;
    }
    getAdapter(instrumentType, gatewayType).then((adapter) => {
      setFields(adapter.fields.filter((f) => f.fieldType === ConnectionFieldType.GATEWAY));
    });
  }, [gatewayType]);

  return {fields};
}

// Per-field client-side validators. Keep in sync with server-side validation so
// the UI surfaces a message before a round-trip, but unrecognized field ids
// fall through to the server error path.
const FIELD_VALIDATORS: Record<string, (value: string) => string | null> = {
  [S3_FIELD_IDS.CUSTOMER_BUCKET]: (v) => (isValidBucket(v) ? null : BUCKET_ERROR),
  [S3_FIELD_IDS.ROLE_ARN]: (v) => (v === '' || isValidRoleArn(v) ? null : ROLE_ARN_ERROR),
};

export interface GatewayFieldError {
  fieldId: string;
  message: string;
}

// Runs client-side validators over the gateway fields and returns the first
// error found, or null if everything is valid.
export function validateGatewayFields(values: Record<string, string>): GatewayFieldError | null {
  for (const [id, value] of Object.entries(values)) {
    const validator = FIELD_VALIDATORS[id];
    if (validator) {
      const message = validator(value.trim());
      if (message) {
        return {fieldId: id, message};
      }
    }
  }
  return null;
}

interface GatewayFieldsFormProps {
  fields: Array<AdapterField>;
  initialValues?: Record<string, string>;
  errorFieldId?: string | null;
  onValuesChange: (values: Record<string, string>) => void;
}

export function GatewayFieldsForm(props: GatewayFieldsFormProps): JSX.Element {
  const {fields, initialValues, errorFieldId, onValuesChange} = props;
  const [values, updateValues] = useReducer(
    (state: Record<string, string>, action: Record<string, string>) => ({...state, ...action}),
    {}
  );

  // Seed values once when the form first renders with a non-null fields array.
  // Callers mount this form inside a modal that is recreated per gateway, so
  // `fields` and `initialValues` are stable for the lifetime of the component;
  // rerunning the seed on every change would clobber in-flight user edits.
  useEffect(() => {
    const seed: Record<string, string> = {};
    for (const field of fields) {
      seed[field.id] = initialValues?.[field.id] ?? field.defaultValue ?? '';
    }
    updateValues(seed);
    onValuesChange(seed);
  }, []); // oxlint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {fields.map((field) => (
        <div key={field.id}>
          <TextField
            label={field.displayName}
            isRequired={field.isRequired}
            value={values[field.id] ?? ''}
            disabled={field.isUneditable}
            hasError={errorFieldId === field.id}
            onChange={(e) => {
              const next = {...values, [field.id]: e.target.value};
              updateValues({[field.id]: e.target.value});
              onValuesChange(next);
            }}
          />
          <br />
        </div>
      ))}
    </>
  );
}
